import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AlertService, LoginSelector, Product, PurchasingService } from 'army-builder-shared';
import { Observable, combineLatest, map, switchMap } from 'rxjs';
import { sortCardPacks } from '../sort-card-packs';
import { CancelSubscriptionComponent } from '../../subscription/cancel-subscription.component';
import { Router } from '@angular/router';
import { SubscriptionService } from '../../subscription/subscription.service';
import { SettingsService } from 'army-builder-shared';
import { Capacitor } from '@capacitor/core';
import { Store, select } from '@ngrx/store';

@Component({
    standalone: false,
    selector: 'fww-subscription-list',
    template: `
        <abs-page-layout
            title="Subscription"
            *ngLet="{
                purchases: purchases$ | async,
                products: products$ | async,
                ownedPacks: ownedPacks$ | async,
                allAccess: allAccess$ | async,
                isSubscriber: isSubscriber$ | async,
                expiry: expiry$ | async,
                freeTrial: freeTrial$ | async
            } as props"
        >
            <div class="content">
                @if (settingsService.isAdmin()) {
                    <admin-entitlement-panel></admin-entitlement-panel>
                }
                <div class="subscription" *ngIf="props.allAccess">
                    <h2>{{ props.allAccess.name | language | async }}</h2>
                    <div class="price">{{ props.allAccess.displayPrice }} / month</div>
                    <div class="productDescription" [innerHTML]="props.allAccess.description | language | async"></div>

                    @if (props.isSubscriber) {
                        <p>
                            If not cancelled, your subscription will auto-renew on {{ props.expiry | date }} at
                            {{ props.allAccess.displayPrice }}.
                        </p>
                        <ion-button color="danger" (click)="cancelSub(props.allAccess)">
                            {{ 'GLOBAL.PURCHASES.CANCEL_SUB' | translate }}
                        </ion-button>
                    } @else if (isWeb) {
                        <div class="coming-soon">
                            <ion-icon name="phone-portrait-outline"></ion-icon>
                            <p class="coming-soon-headline">Coming soon to the web</p>
                            <p>Subscribe via the iOS or Android app.</p>
                        </div>
                    } @else {
                        @if (props.freeTrial && props.allAccess.supportsFreeTrial) {
                            <ion-card>
                                <ion-card-content>{{ 'GLOBAL.SUBSCRIPTION.FREE_TRIAL' | translate }}</ion-card-content>
                            </ion-card>
                        }
                        @if (!isWeb) {
                            <ion-button (click)="purchase(props.allAccess.key)">
                                {{ 'GLOBAL.PURCHASES.SUBSCRIBE' | translate }} - {{ props.allAccess.displayPrice }} /
                                {{ 'GLOBAL.PURCHASES.MONTH' | translate }}
                            </ion-button>
                        } @else {
                            <fww-subscribe [product]="props.allAccess"></fww-subscribe>
                        }
                    }
                </div>

                <div class="support-actions">
                    <p>If your subscription or purchases aren't reflected in the app, tap the button below to restore them.</p>
                    <div class="buttons">
                        <ion-button (click)="restoreAndRefresh()">{{ 'GLOBAL.PURCHASES.RESTORE' | translate }}</ion-button>
                    </div>
                </div>

                @if ((props.ownedPacks ?? []).length > 0) {
                    <h2>Your FWW Card Packs</h2>
                    <p class="cardPackNotice">
                        Card packs are no longer available for purchase. Your existing purchases remain active and you'll continue to have
                        access to unlocked content.
                    </p>
                    <div class="owned-packs">
                        @for (product of props.ownedPacks; track product.key) {
                            <div class="owned-pack">
                                <ion-icon name="checkmark-circle" class="owned-pack-icon"></ion-icon>
                                <div class="owned-pack-info">
                                    <span class="owned-pack-name">{{ product.name | language | async }}</span>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        </abs-page-layout>
    `,
    styles: [
        `
            .owned-packs {
                padding: 0 12px;
            }

            .owned-pack {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            }

            .owned-pack:last-child {
                border-bottom: none;
            }

            .owned-pack-icon {
                font-size: 1.3em;
                color: #6abf69;
                flex-shrink: 0;
            }

            .owned-pack-name {
                font-size: 0.95em;
            }

            .subscription {
                background: url(/assets/ff/images/sunburst.jpg) no-repeat;
                background-size: cover;
                background-position: center;
                text-align: center;
                padding: 10px;
                border: 4px solid #bc4039;
            }

            .subscription h2 {
                margin: 0;
                padding: 10px;
                font-size: 1.2em;
            }

            .price {
                padding: 10px 20px 0;
                font-size: 1.2em;
                font-weight: bold;
            }

            .productDescription {
                padding: 10px 20px;
            }

            .productDescription ::ng-deep p {
                margin: 0 0 12px;
                text-align: center;
            }

            .productDescription ::ng-deep ul {
                padding-left: 20px;
                text-align: left;
            }

            .cardPackNotice {
                font-size: 0.9em;
                color: #666;
                padding: 0 12px;
                margin-bottom: 12px;
            }

            .support-actions {
                margin: 20px 0 8px;
            }

            .support-actions p {
                font-size: 0.9em;
                color: #888;
                margin: 0 0 8px;
            }

            .restore-note {
                font-size: 0.82em;
                color: #888;
                padding: 0 12px;
                margin: 6px 0 16px;
                line-height: 1.45;
            }

            .restore-note a {
                color: #f8e6c0;
            }

            .coming-soon {
                margin: 16px 0 8px;
                padding: 16px;
                background: rgba(0, 0, 0, 0.45);
                border: 2px solid #bc4039;
                border-radius: 4px;
            }

            .coming-soon ion-icon {
                font-size: 2.4em;
                color: #f8e6c0;
                margin-bottom: 4px;
            }

            .coming-soon-headline {
                font-size: 1.15em;
                font-weight: bold;
                color: #f8e6c0;
                margin: 4px 0 6px;
            }

            .coming-soon p {
                color: #ddd;
                margin: 0;
                font-size: 0.95em;
            }
        `
    ]
})
export class SubscriptionListComponent {
    public platform = Capacitor.getPlatform();
    public isWeb = this.platform === 'web';

    public purchases$ = this.purchasingSvc.purchases$;
    public products$ = this.purchasingSvc.products$.pipe(
        map((products) => [...products].sort(sortCardPacks).filter((p) => p.key !== 'all_access' && p.canPurchase))
    );

    /** Card packs the user has already purchased (NON_CONSUMABLE only) */
    public ownedPacks$ = combineLatest([this.purchasingSvc.products$, this.purchasingSvc.purchases$]).pipe(
        map(([products, purchases]) =>
            products
                .filter((p) => p.type === 'NON_CONSUMABLE' && p.key !== 'all_access')
                .filter((p) => purchases.includes(p.key) || p.bundles?.some((b) => purchases.includes(b)))
                .sort(sortCardPacks)
        )
    );

    public allAccess$ = this.purchasingSvc.products$.pipe(
        map((products) => {
            return products.find((p) => p.key === 'all_access');
        })
    );
    public isSubscriber$ = this.allAccess$.pipe(
        switchMap((allAccess) => {
            return this.isOwned(allAccess);
        })
    );

    public status$ = this.purchasingSvc.status$;

    freeTrial$ = this.subscriptionService.freeTrial$;
    expiry$ = this.subscriptionService.expiry$;
    expiryProgress$ = this.expiry$.pipe(
        map((e) => {
            const daysRemaining = Math.floor((e.getTime() - Date.now()) / 1000 / 60 / 60 / 24);
            return daysRemaining / 28;
        })
    );

    public debug = false;

    constructor(
        public purchasingSvc: PurchasingService,
        private modalController: ModalController,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private alertService: AlertService,
        public settingsService: SettingsService,
        private store: Store
    ) {
        // setTimeout(() => {
        //     this.restorePurchases();
        // }, 200);
    }

    purchase(productId: string) {
        this.purchasingSvc.purchase(productId);
    }

    async restoreAndRefresh() {
        if (!this.isWeb) {
            const result = await this.purchasingSvc.restorePurchases();
            if (!result.success) {
                await this.alertService.showAlert(
                    'Restore Failed',
                    'Unable to restore purchases from the store. Please check your connection and try again, or contact support at fallout@maloric.com.'
                );
                return;
            }
        }
        await this.purchasingSvc.refreshUserPurchases();
        this.router.navigateByUrl('/subscription/restore');
    }

    isOwned(product: Product): Observable<boolean> {
        return this.purchasingSvc.purchases$.pipe(
            map((purchases) => {
                return !!purchases.includes(product?.key) || !!purchases.find((purchase) => product?.bundles?.includes(purchase));
            })
        );
    }

    async cancelSub(product: Product) {
        const modal = await this.modalController.create({
            component: CancelSubscriptionComponent
        });

        modal.present();
    }

    refresh() {
        window.location.href = window.location.href;
    }
}
